/**
 * What plays the pinyin chart's recordings.
 *
 * **One `Audio` for the page.** A second press, on any control, replaces what
 * is sounding, so a reader working along a row never hears two syllables over
 * each other. `src/site/audio-controls.ts` settled the same thing for the 词典.
 *
 * **A run of files is one request to stop.** Pressing a syllable plays its
 * four tones in turn, and pressing another while the second is sounding has to
 * end the run as well as the file. Every run takes a number, and a run whose
 * number is no longer the latest stops at its next step.
 */

/** The part of `HTMLAudioElement` the player uses, so a test can stand in. */
export interface PlayableAudio {
  src: string;
  play(): Promise<void>;
  pause(): void;
  addEventListener(type: "ended" | "error", listener: () => void): void;
  removeEventListener(type: "ended" | "error", listener: () => void): void;
}

/** One step of a run: a file, and what to do as it starts. */
export interface PlayStep {
  readonly src: string;
  readonly onStart?: () => void;

  /**
   * Milliseconds of quiet before this file, where the run's usual gap is
   * wrong. The syllables of one word follow each other with none. Given on a
   * run's first file, it waits before that too, which is how a run that
   * carries on from the last keeps the gap between them.
   */
  readonly pauseBefore?: number;
}

/** How a run ended. */
export type PlayOutcome = "finished" | "stopped" | "failed";

/** Milliseconds of quiet between two files in a run. */
const gapBetween = 350;

export class ChartPlayer {
  readonly #audio: PlayableAudio;
  readonly #wait: (milliseconds: number) => Promise<void>;
  #run = 0;

  /**
   * @param audio - The element that plays. One for the page.
   * @param wait - How a gap is waited out, which a test makes instant.
   */
  constructor(
    audio: PlayableAudio,
    wait: (milliseconds: number) => Promise<void> = (
      milliseconds,
    ): Promise<void> =>
      new Promise((resolve) => {
        setTimeout(resolve, milliseconds);
      }),
  ) {
    this.#audio = audio;
    this.#wait = wait;
  }

  /**
   * Play files one after another, stopping whatever was playing first.
   * @param steps - The files, in order.
   * @returns How the run ended.
   */
  async play(steps: readonly PlayStep[]): Promise<PlayOutcome> {
    this.#run += 1;
    const run = this.#run;

    this.#audio.pause();

    for (const [at, step] of steps.entries()) {
      if (at > 0 || step.pauseBefore !== undefined) {
        // oxlint-disable-next-line no-await-in-loop -- a run is in order: each file waits for the one before it
        await this.#wait(step.pauseBefore ?? gapBetween);
      }

      if (run !== this.#run) {
        return "stopped";
      }

      step.onStart?.();

      // oxlint-disable-next-line no-await-in-loop -- a run is in order: each file waits for the one before it
      const played = await this.#playOne(step.src, run);

      if (played !== "finished") {
        return played;
      }
    }

    return "finished";
  }

  /** Stop whatever is playing, and any run it belongs to. */
  stop(): void {
    this.#run += 1;
    this.#audio.pause();
  }

  #playOne(src: string, run: number): Promise<PlayOutcome> {
    return new Promise((resolve) => {
      const audio = this.#audio;
      const settle = (outcome: PlayOutcome): void => {
        audio.removeEventListener("ended", ended);
        audio.removeEventListener("error", failed);
        clearInterval(watch);
        resolve(outcome);
      };
      const ended = (): void => {
        settle(run === this.#run ? "finished" : "stopped");
      };
      const failed = (): void => {
        settle(run === this.#run ? "failed" : "stopped");
      };
      // A paused file fires neither event, so a stopped run is noticed here.
      const watch = setInterval(() => {
        if (run !== this.#run) {
          settle("stopped");
        }
      }, 100);

      audio.addEventListener("ended", ended);
      audio.addEventListener("error", failed);
      audio.src = src;
      audio.play().catch(failed);
    });
  }
}
